<template>
	<ejs-pdfviewer
		v-if="selectedDocument?.id"
		id="pdfviewer"
		ref="pdfviewer"
		:class="classes"
		:document-load="documentLoad"
		:document-unload="documentUnload"
		:service-url="serviceUrl"
		:document-path="documentPath"
		:interaction-mode="interactionMode"
		:enable-toolbar="true"
		:enable-annotation-toolbar="false"
		:enable-magnification="true"
		:enable-navigation="true"
		:enable-navigation-toolbar="enablePagination"
		:enable-thumbnail="enablePagination"
		:enable-text-search="false"
		:enable-form-fields="false"
		:is-thumbnail-view-open="true"
		:enable-text-selection="true"
		:enable-annotation="true"
		:enable-desktop-mode="true"
		:ajax-request-settings="ajaxRequestSettings"
		context-menu-option="None"
		:annotation-add="annotationAdd"
		:annotation-move="annotationMove"
		:annotation-resize="annotationResize"
		:annotation-properties-change="annotationPropertiesChange"
		:annotation-select="annotationSelect"
		:annotation-un-select="annotationUnSelect"
		:annotation-double-click="annotationDoubleClick"
		:is-annotation-toolbar-open="true"
		:enable-comment-panel="false"
		:signature-field-settings="signatureFieldSettings"
		:initial-field-settings="initialFieldSettings"
		:ajax-request-failed="ajaxRequestFailed"
	/>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import { PdfViewerComponent } from "@syncfusion/ej2-vue-pdfviewer";
import { DisplayMode, AnnotationResizerLocation } from "@syncfusion/ej2-pdfviewer";
import patchPdfViewer from "../../pdfViewer/patchPdfviewer";
import TaskTypes from "../../models/taskTypes.js";
import AnnotationType from "../../models/annotationType.js";
import * as Annotations from "./Annotations.js";
import { isMobileUA } from "../../helpers/mobile";

const PAN_MODE = `Pan`;
const SELECTION_MODE = `TextSelection`;

// pagination sidebar is disabled on mobile
const enablePagination = () => !isMobileUA();

const moveSyncfusionElements = () => {
	if(enablePagination()) {
		const ej2Sidebar = document.getElementById(`pdfviewer_sideBarContentContainer`);
		document.getElementById(`pagination`).appendChild(ej2Sidebar);

		const currentPageInput = document.getElementById(`pdfviewer_currentPageInputContainer`);
		const totalPages = document.getElementsByClassName(`total-page-container`)[0];
		document.querySelector(`#pagination .e-toolbar-center`).insertBefore(currentPageInput, totalPages);
	}
	const zoomDropDown = document.getElementById(
		`pdfviewer_zoomDropDownContainer`
	);
	const zoomIn = document.getElementsByClassName(`e-toolbar-item zoom-in`)[0];
	document.querySelector(`#customToolbar .e-toolbar-left`).insertBefore(zoomDropDown, zoomIn);
};

let paginationTimeout = 0;

const paginationElements = [`_sideBarContent`, `_sideBarContentContainer`, `_viewerContainer`, `_pageViewContainer`,
	`_thumbnail_view`, `_sideBarResizer`, `_sideBarTitleContainer`, `_sideBarContentSplitter`];

function canOpenPagination() {
	return !paginationElements.map(element => document.getElementById(`pdfviewer${element}`)).some(element => !element);
}

const shouldOpenPagination = () => `none` === document.getElementById(`pdfviewer_sideBarContentContainer`)?.style?.display || `none` !== document.getElementById(`pdfviewer_sideBarTitleContainer`)?.style?.display;

function openPagination(pdfViewer) {
	if(!enablePagination()) return;
	const navigationPane = pdfViewer?.viewerBase?.navigationPane;
	if (!canOpenPagination()) {
		paginationTimeout = setTimeout(() => openPagination(pdfViewer), 500);
	} else if (shouldOpenPagination()) {
		navigationPane?.openThumbnailPane?.();
		paginationTimeout = setTimeout(() => openPagination(pdfViewer), 500);
	} else {
		paginationTimeout = setTimeout(() => openPagination(pdfViewer), 2500);
	}
}

const setSelectionSettings = (settings) => {
	settings.selectionBorderColor = Annotations.selectColor;
	settings.selectorLineDashArray = Annotations.selectorLineDashArray;
};

const setResizerSettings = (settings) => {
	settings.resizerFillColor = Annotations.selectColor;
	settings.resizerBorderColor = Annotations.selectColor;
	settings.resizerShape = Annotations.resizerShape;
	settings.resizerLocation = AnnotationResizerLocation.Corners;
};

const setPdfViewerSettings = (viewer) => {
	patchPdfViewer(viewer);
	// minHeight enables proportional scaling for signature and image annotations
	viewer.annotationSettings.minHeight = 1;
	// Custom stamp selector settings
	const selectorSettings = viewer.annotationSelectorSettings;
	setSelectionSettings(selectorSettings);
	setResizerSettings(selectorSettings);
	const textSettings = viewer.freeTextSettings;
	//auto fit automatically resized 'Add Text' annotation during text input to keep it in one line
	textSettings.enableAutoFit = true;
	const textSelector = textSettings.annotationSelectorSettings;
	setSelectionSettings(textSelector);
	setResizerSettings(textSelector);
	const lineSettings = viewer.lineSettings;
	setResizerSettings(lineSettings.annotationSelectorSettings);
	lineSettings.thickness = 2;
	const inkSettings = viewer.inkAnnotationSettings.annotationSelectorSettings;
	setSelectionSettings(inkSettings);
	setResizerSettings(inkSettings);
	viewer.handWrittenSignatureSettings.typeSignatureFonts = [`Dancing Script`, `Homemade Apple`, `Pacifico`, `Pinyon Script`];
};

const signatureDialogSettings = {
	displayMode: DisplayMode.Draw | DisplayMode.Text,
	hideSaveSignature: true,
};

// Default values from Syncfusion
const signatureIndicatorSettings = { opacity: 1, backgroundColor: `orange`, width: 19, height: 10, fontSize: 10, text: null, color: `black` };

export default {
	components: {
		"ejs-pdfviewer": PdfViewerComponent,
	},
	inject: {
		getSelectedAnnotationId: { type: Function },
		getAnnotationById: { type: Function },
		selectAnnotation: { type: Function },
		showSignatureModal: { type: Function },
		showInitialsModal: { type: Function },
		completeTask: { type: Function },
		verticalOffset: { type: Number }
	},
	props: {
		selectedDocument: { type: Object, required: true },
		isPanMode: { type: Boolean, required: true },
		pdfViewer: { type: Object, required: true },
	},
	emits: [`update:pdfViewer`, `update:isPanMode`, `pdfViewerError`],
	data() {
		return {
			serviceUrl: `${window.Notary.Config.ApiUrlBase}/Api/Notarization/${window.Notary.NotarizationId}/PdfViewer`,
			signatureFieldSettings: { signatureDialogSettings, signatureIndicatorSettings },
			initialFieldSettings: {
				initialDialogSettings: signatureDialogSettings,
			},
		};
	},
	computed: {
		interactionMode() {
			return this.isPanMode ? PAN_MODE : SELECTION_MODE;
		},
		documentPath() {
			return `${this.selectedDocument.id}`;
		},
		classes() {
			let classes = [`e-control`, `e-pdfviewer`, `e-lib`];
			if (this.selectedDocument?.isReadonly) classes.push(`readonly`);
			if (this.selectedDocument?.isDeclined) classes.push(`declined`);
			return classes.join(` `);
		},
		ajaxRequestSettings: () => ({
			ajaxHeaders: [
				{
					headerName: `Authorization`,
					headerValue: `bearer ${sessionStorage[`notarizationApiToken`]}`,
				},
			],
		}),
		signers() {
			const signers = (signers, signerClass) =>
				signers.map(({ notarizationSignerId, firstName, lastName, address, city, regionCode, postalCode }, idx) => ({
					id: notarizationSignerId,
					firstName: firstName,
					lastName: lastName,
					initials: `${firstName[0]}${lastName[0]}`,
					fullName: `${firstName} ${lastName}`,
					address: address,
					city: city,
					regionCode: regionCode,
					postalCode: postalCode,
					isNotary: false,
					signerClass: `${signerClass}${idx % 3 + 1}`
				}));
			return [{ ...this.notary, isNotary: true, signerClass: `notary` },
				...signers(this.principalSigners, `signer`),
				...signers(this.witnesses, `witness`),];
		},	
		enablePagination,
		...mapState([`author`, `notary`, `notarialActsTemplates`]),
		...mapGetters(`Signers`, [`principalSigners`, `witnesses`]),
	},
	mounted() {
		window.addEventListener(`beforeunload`, this.onBeforeWindowUnload);
	},
	unmounted() {
		window.removeEventListener(`beforeunload`, this.onBeforeWindowUnload);
	},
	methods: {
		documentLoad() {
			moveSyncfusionElements();
			const pdfViewer = this.$refs.pdfviewer.ej2Instances;
			pdfViewer.element.querySelector(`#pdfviewer_viewerContainer`).scroll({
				top: this.verticalOffset.value,
				left: 0,
				behavior: `instant`
			});
			setPdfViewerSettings(pdfViewer);
			this.$emit(`update:pdfViewer`, pdfViewer);
			this.documentLoaded(this.selectedDocument?.isComplete);
			paginationTimeout = setTimeout(() => openPagination(pdfViewer), 500);
		},
		documentUnload() {
			clearTimeout(paginationTimeout);
		},
		annotationAdd(event) {
			const annotation = this.getAnnotationById(event.annotationId);
			const isSelected = this.getSelectedAnnotationId() == event.annotationId;
			if (!isSelected) {
				this.selectAnnotation(annotation);
			}
			const viewer = this.pdfViewer;
			if (annotation.shapeAnnotationType === AnnotationType.FreeText) {
				// UI added text annotations are slightly different from imported annotation.
				// To make annotations consistent, every UI added text annotation is removed and readded.
				viewer.annotationModule.deleteAnnotation();
				viewer.addAnnotation(annotation);
			}
			// clean up stamp settings
			viewer.customStampSettings.isLock = false;
			this.syncAnnotationAdd(annotation);
		},
		annotationMove(event) {
			this.syncAnnotationUpdate(this.getAnnotationById(event.annotationId));
		},
		annotationResize(event) {
			this.syncAnnotationUpdate(this.getAnnotationById(event.annotationId));
		},
		annotationPropertiesChange(event) {
			let annotation = this.getAnnotationById(event.annotationId);
			// If annotation was not found, abort
			if(!annotation) return;
			const isEmptyAddTextAnnotation = event.annotationType === AnnotationType.FreeText && event.isFreeTextChanged && /^\s*$/.test(event.currentText);
			if (isEmptyAddTextAnnotation) {
				// restore 'Type Here' placeholder for empty annotation
				const placeholder = Annotations.customTextContent;
				this.pdfViewer.annotationModule.editAnnotation({ ...annotation, content: placeholder });
				annotation.dynamicText = placeholder;
			}
			this.syncAnnotationUpdate(annotation);
		},
		async annotationSelect(event) {
			const task = this.getAnnotationById(event.annotationId);
			const customData = task?.customData;
			this.$emit(`update:isPanMode`, false);

			if (task?.author != this.author && customData?.isTask && !customData?.isComplete) {
				switch (customData.taskType) {
				case TaskTypes.FULL_NAME:
					this.completeTask(task, new Annotations.FreeText(task, this.author, task.customData.fullName));
					break;
				case TaskTypes.TODAYS_DATE:
					this.completeTask(task, new Annotations.FreeText(task, this.author, Annotations.todaysDate()));
					break;
				case TaskTypes.TODAYS_DAY:
					this.completeTask(task, new Annotations.FreeText(task, this.author, Annotations.todaysDay()));
					break;
				case TaskTypes.TODAYS_MONTH:
					this.completeTask(task, new Annotations.FreeText(task, this.author, Annotations.todaysMonth()));
					break;
				case TaskTypes.TODAYS_YEAR:
					this.completeTask(task, new Annotations.FreeText(task, this.author, Annotations.todaysYear()));
					break;
				case TaskTypes.NA:
					this.completeTask(task, new Annotations.FreeText(task, this.author, `N/A`));
					break;
				case TaskTypes.ADD_TEXT:
					this.completeTask(task, new Annotations.FreeText(task, this.author));
					break;
				case TaskTypes.BLACK_LINE:
					this.completeTask(task, new Annotations.BlackLine(task, this.author));
					break;
				case TaskTypes.CHECKMARK:
					this.completeTask(task, await Annotations.Checkmark.build(task, this.author));
					break;
				case TaskTypes.CIRCLE_TOOL:
					this.completeTask(task, await Annotations.CircleTool.build(task, this.author));
					break;
				case TaskTypes.CROSS:
					this.completeTask(task, await Annotations.Cross.build(task, this.author));
					break;
				case TaskTypes.ADDRESS:
					this.completeTask(task, new Annotations.FreeText(task, this.author, this.signerAddress(task.customData)));
					break;
				
				case TaskTypes.SIGNATURE: {
					this.showSignatureModal(task);
					break;
				}
				case TaskTypes.INITIALS: {
					this.showInitialsModal(task);
					break;
				}
				default:
					console.error(`Wrong task type: ${customData.taskType}`);
				}
			}
		},
		annotationUnSelect() {
			this.$emit(`update:isPanMode`, true);
		},
		annotationDoubleClick(event) {
			const annotation = this.getAnnotationById(event.annotationId);
			// Changes the color of custom text annotation from gray to black.
			if (annotation && !annotation.isReadonly && event.annotation.content == Annotations.customTextContent && annotation.fontColor != Annotations.textFontColor) {
				this.pdfViewer.annotation.modifyFontColor(Annotations.textFontColor);
			}
		},
		ajaxRequestFailed(event) {
			this.$emit(`pdfViewerError`, event);
		},
		onBeforeWindowUnload() {
			this.pdfViewer?.destroy?.();
		},
		signerAddress(event) {
			for (let i in this.signers) {				
				if (this.signers[i].id === event.signerId) {
					return this.signers[i].address + `, ` + this.signers[i].city + `, ` + this.signers[i].regionCode + `, ` + this.signers[i].postalCode;
				}
			}
		},
		...mapActions(`AnnotationsSync`, [
			`syncAnnotationUpdate`,
			`syncAnnotationAdd`,
			`syncAnnotationRemove`,
			`documentLoaded`
		]),
	},
};
</script>